/**
 * Preview Components 工具函数集合
 */

import type {TypeResponseSchema} from '@/types/common';
import type {AssetBundle} from '@alilc/lowcode-utils';
import type {AxiosRequestConfig} from 'axios';

import {adapterEnhancer} from '@big-data/react-forge';
import cloneDeep from 'lodash/cloneDeep';
import toast from 'react-hot-toast';

import {Request, type MessageHandler} from '@/services/httpWithoutUI';

/**
 * ============================================
 * 飞书环境工具函数
 * ============================================
 */

/**
 * 判断当前环境是否为飞书应用
 * @returns {boolean} 是否在飞书环境中
 */
export const isFeishuApp = (): boolean => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('lark') || ua.includes('feishu');
};

/**
 * 获取当前客户端类型
 * @returns {string} 客户端类型：'feishu' | 'dingtalk' | 'wechat' | 'web'
 */
export const getClientType = (): string => {
    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes('lark') || ua.includes('feishu')) {
        return 'feishu';
    }
    if (ua.includes('dingtalk')) {
        return 'dingtalk';
    }
    if (ua.includes('micromessenger')) {
        return 'wechat';
    }

    return 'web';
};

/**
 * ============================================
 * URL 参数工具函数
 * ============================================
 */

/**
 * 返回 url 中全部 query 内容
 * 获取全部参数的值
 */
export function getURLParams(url = window.location.href): Record<string, string> {
    const params = new URLSearchParams(url.slice(url.indexOf('?') + 1));
    const paramsObj: Record<string, string> = {};

    for (const [key, value] of params.entries()) {
        paramsObj[key] = value;
    }

    return paramsObj;
}

/**
 * 获取环境配置
 */
export const getProcessEnv = () => {
    const ENV_MAP: Record<string, string> = {
        dev: 'test',
        test: 'test',
        beta: 'prod',
        prod: 'prod',
    };

    return ENV_MAP[process.env.ENV] || 'prod';
};

/**
 * ============================================
 * 资源加载工具函数
 * ============================================
 */

/**
 * 检查资源是否已经加载完成
 */
export const isAssetLoaded = (url: string): boolean => {
    // 检查 script 标签
    const scripts = document.querySelectorAll('script[src]');
    for (let i = 0; i < scripts.length; i++) {
        const script = scripts[i] as HTMLScriptElement;
        if (script.src === url) {
            return true;
        }
    }

    // 检查 link 标签 (CSS)
    const links = document.querySelectorAll('link[href]');
    for (let i = 0; i < links.length; i++) {
        const link = links[i] as HTMLLinkElement;
        if (link.href === url) {
            return true;
        }
    }

    return false;
};

/**
 * 检查资源数组中的所有资源是否都已加载完成
 */
export const areAllAssetsLoaded = (assets: string[]): boolean => {
    return assets.every((asset) => isAssetLoaded(asset));
};

/**
 * 从 AssetBundle 对象中提取 URL 数组
 */
export const extractUrlsFromAssetBundle = (bundle: AssetBundle): string[] => {
    if (bundle && Array.isArray(bundle.assets)) {
        return bundle.assets
            .map((asset) => {
                // 处理不同类型的 asset
                if (typeof asset === 'string') {
                    return asset;
                }
                if (asset && typeof asset === 'object' && 'content' in asset) {
                    return asset.content as string;
                }
                return null;
            })
            .filter((url): url is string => Boolean(url));
    }

    return [];
};

/**
 * ============================================
 * Schema 相关工具函数
 * ============================================
 */

/**
 * 获取组件的 unitId
 */
const getUnitId = (params: any) => params?.props?.settings?.unitId;

/**
 * 处理 Tabs 组件
 */
const handleTabs = (list: any[], idList: string[]) => {
    for (let index = list.length - 1; index >= 0; index--) {
        const element = list[index];
        // slot 未配置 value 就是不存在的
        const childList = element.children.value || [];

        // 处理 tab 下的组件
        for (let index = childList.length - 1; index >= 0; index--) {
            const child = childList[index];
            const unitIt = getUnitId(child);

            if (unitIt && idList.includes(unitIt)) {
                childList.splice(index, 1);
            }
        }

        // tab 下的组件为空,则删除这个 tab
        if (childList.length === 0) {
            list.splice(index, 1);
        }
    }
};

/**
 * 删除无权限的组件
 */
export const checkComponentPermission = (projectSchema: any, idList: string[]) => {
    const last = cloneDeep(projectSchema);

    const componentList = last.componentsTree[0]?.children[0]?.children || [];

    for (let index = componentList.length - 1; index >= 0; index--) {
        const element = componentList[index];

        // 容器组件都需要单独处理
        const isTabs = element?.componentName === 'SingleTabs';
        // const isTabs = element?.props?.list?.length > 0;
        if (isTabs) {
            handleTabs(element.props.list, idList);
            // tabs 被清空则删除 tabs 组件
            if (element.props.list.length === 0) {
                componentList.splice(index, 1);
                // console.log('删除数组', index);
            }
            continue;
        }

        const isSwipper = element?.componentName === 'Swipper';
        if (isSwipper) {
            element.props.pageList = element?.props?.pageList?.filter((item: any) => item);
            element.props.pageList.forEach((item: any) => {
                item.subList = item?.subList?.filter((item: any) => {
                    if (item?.component?.value) {
                        const unitIt = getUnitId(item?.component.value[0]);
                        return !idList.includes(unitIt);
                    }
                    return false;
                });
            });
            element.props.pageList = element.props.pageList.filter((item: any) => item.subList.length > 0);
            // 如果轮播组件中的所有页面都被删除,则删除整个组件
            if (element.props.pageList.length === 0) {
                componentList.splice(index, 1);
            }

            continue;
        }

        const isCombinationChart = element?.componentName === 'CombinationContainer';
        // 组合容器,内部删除无权限组件即可,自身可以为空
        if (isCombinationChart) {
            element.children = element?.children?.filter((item: any) => {
                const unitIt = getUnitId(item);
                return !idList.includes(unitIt);
            });

            continue;
        }

        // 正常组件
        const unitIt = getUnitId(element);
        if (unitIt && idList.includes(unitIt)) {
            componentList.splice(index, 1);
            continue;
            // console.log('删除数组', index);
        }
    }

    // console.log('last: ', last);
    return last;
};

/**
 * 向根组件的 props 中添加 customPublicFilterRuleList 属性
 */
export const addCustomPublicFilterRuleList = (schema: any, customPublicFilterRuleList: any) => {
    // 根组件名称列表
    const COMPONENTS_ROOT_NAME = ['CompositeRootContent', 'RootContent', 'RootContentNew'];

    if (schema?.componentsTree?.[0]?.children) {
        const addToComponents = (components: any) => {
            if (Array.isArray(components)) {
                components.forEach((component: any) => {
                    // 检查是否是根组件
                    if (COMPONENTS_ROOT_NAME.includes(component.componentName)) {
                        // 确保 props 对象存在
                        if (!component.props) {
                            component.props = {};
                        }
                        // 添加 customPublicFilterRuleList 属性
                        component.props.customPublicFilterRuleList = customPublicFilterRuleList;
                    }

                    // 递归处理子组件
                    if (component.children) {
                        addToComponents(component.children);
                    }
                });
            }
        };

        addToComponents(schema.componentsTree[0].children);
    }
    return schema;
};

/**
 * ============================================
 * HTTP 客户端实例
 * ============================================
 */

// 创建使用 toast 的消息处理器
const toastMessageHandler: MessageHandler = {
    error: (msg: string) => toast.error(msg),
    success: (msg: string) => toast.success(msg),
};

// 创建专用于 preview 的 HTTP 客户端
const previewHttpClient = new Request(
    {
        timeout: 300000,
        requestOptions: {
            globalErrorMessage: true,
            globalSuccessMessage: false,
        },
        withCredentials: true,
        adapter: adapterEnhancer(),
    },
    toastMessageHandler,
);

/**
 * C端预览报表接口
 * 获得报表 schema 信息
 */
export const serveGetSchema = (
    params: {
        dashboardId?: string;
        // 区分是否配置端
        isConfig: boolean;
        // 配置的版本
        versionId?: string;
        // 门户 id
        homeId?: string;
    },
    config?: AxiosRequestConfig<any>,
) => {
    return previewHttpClient.post<TypeResponseSchema>('/uanalysis-intelligence/config/dashBoard', params, config);
};
